import { useState } from "react";
import {
  Box, Typography, Paper, TextField, Button, List, ListItem,
  ListItemText, ListItemSecondaryAction, IconButton, CircularProgress, Divider,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import {
  useGetCmsCategoriesQuery,
  useCreateCmsCategoryMutation,
  useDeleteCmsCategoryMutation,
} from "../../../app/websiteApiSlice";
import toast from "../../../helpers/toast";

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim();
}

export default function CmsCategories() {
  const { data: categories = [], isLoading } = useGetCmsCategoriesQuery();
  const [createCategory, { isLoading: creating }] = useCreateCmsCategoryMutation();
  const [deleteCategory, { isLoading: deleting }] = useDeleteCmsCategoryMutation();

  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const [slugTouched, setSlugTouched] = useState(false);

  function handleNameChange(e) {
    const name = e.target.value;
    setForm((f) => ({
      ...f,
      name,
      slug: slugTouched ? f.slug : slugify(name),
    }));
  }

  async function handleCreate() {
    if (!form.name || !form.slug) { toast.error("Name and slug are required"); return; }
    try {
      await createCategory(form).unwrap();
      toast.success("Category created");
      setForm({ name: "", slug: "", description: "" });
      setSlugTouched(false);
    } catch (err) {
      toast.error(err?.message || "Failed to create category");
    }
  }

  async function handleDelete(cat) {
    if (!window.confirm(`Delete category "${cat.name}"? Posts will not be deleted.`)) return;
    try {
      await deleteCategory(cat.id).unwrap();
      toast.success("Category deleted");
    } catch (err) {
      toast.error(err?.message || "Failed to delete");
    }
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>Categories</Typography>

      <Box display="flex" gap={3} flexWrap="wrap">
        {/* Create form */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, flex: "0 0 320px" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Add New Category</Typography>
          <TextField
            label="Name"
            value={form.name}
            onChange={handleNameChange}
            fullWidth size="small" sx={{ mb: 1.5 }}
          />
          <TextField
            label="Slug"
            value={form.slug}
            onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: e.target.value })); }}
            fullWidth size="small" sx={{ mb: 1.5 }}
          />
          <TextField
            label="Description (optional)"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth size="small" multiline rows={2} sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            disabled={creating}
            fullWidth
          >
            {creating ? "Creating..." : "Add Category"}
          </Button>
        </Paper>

        {/* Categories list */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, flex: 1, minWidth: 280 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>All Categories</Typography>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
          ) : (
            <List dense disablePadding>
              {categories.length === 0 && (
                <Typography variant="body2" color="text.secondary">No categories yet.</Typography>
              )}
              {categories.map((cat, index) => (
                <Box key={cat.id}>
                  {index > 0 && <Divider />}
                  <ListItem disableGutters>
                    <ListItemText
                      primary={cat.name}
                      secondary={`/blog/category/${cat.slug}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(cat)}
                        disabled={deleting}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
